import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, inject, input, OnDestroy } from '@angular/core';
import { AiUpsellPopoverComponent } from '@shared/components/ai-upsell-popover/ai-upsell-popover.component';

/**
 * Shows a Foodback Intelligence upsell popover while the host is hovered.
 * Bind the value to control when it's active (e.g. only when the feature is
 * unavailable): `<span [appAiUpsellPopover]="!aiAvailable()">`. A short grace
 * period on leave lets the pointer travel into the popover to click its link.
 */
@Directive({
  selector: '[appAiUpsellPopover]',
  host: {
    '(mouseenter)': 'onHostEnter()',
    '(mouseleave)': 'onHostLeave()',
    '(focusin)': 'onHostEnter()',
    '(focusout)': 'onHostLeave()',
  },
})
export class AiUpsellPopoverDirective implements OnDestroy {
  /** When false the popover is suppressed (feature available → host usable normally). */
  readonly enabled = input(true, { alias: 'appAiUpsellPopover' });

  private readonly overlay = inject(Overlay);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private overlayRef: OverlayRef | null = null;
  private portal: ComponentPortal<AiUpsellPopoverComponent> | null = null;
  private isHostHovered = false;
  private isPopoverHovered = false;

  protected onHostEnter(): void {
    this.isHostHovered = true;
    if (this.enabled()) {
      this.open();
    }
  }

  protected onHostLeave(): void {
    this.isHostHovered = false;
    this.scheduleClose();
  }

  private open(): void {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        positionStrategy: this.overlay
          .position()
          .flexibleConnectedTo(this.host.nativeElement)
          .withPositions([
            { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
            { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          ]),
      });

      this.overlayRef.overlayElement.addEventListener('mouseenter', () => {
        this.isPopoverHovered = true;
      });
      this.overlayRef.overlayElement.addEventListener('mouseleave', () => {
        this.isPopoverHovered = false;
        this.scheduleClose();
      });
    }

    this.portal ??= new ComponentPortal(AiUpsellPopoverComponent);

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(this.portal);
    }
  }

  private scheduleClose(): void {
    setTimeout(() => {
      if (!this.isHostHovered && !this.isPopoverHovered) {
        this.overlayRef?.detach();
      }
    }, 150);
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
